import React from 'react';
import { X, Calendar, Sparkles, Play } from 'lucide-react';
import { getSuggestionsForDate } from '../../utils/schedulerAI';
import { getFatigueColor, getFatigueLabel } from '../../utils/fatigueCalculator';

/**
 * GapSuggestions Component
 * Modal/popover showing AI-suggested creatives for a gap date
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {function} props.onClose - Callback to close the modal
 * @param {Date} props.targetDate - The gap date to fill
 * @param {Array} props.creatives - All available creatives
 * @param {Array} props.scheduledPosts - All scheduled posts
 * @param {function} props.onSchedule - Callback when user selects a creative to schedule
 */
const GapSuggestions = ({
    isOpen,
    onClose,
    targetDate,
    creatives = [],
    scheduledPosts = [],
    onSchedule
}) => {
    if (!isOpen || !targetDate) return null;

    const suggestions = getSuggestionsForDate(targetDate, creatives, scheduledPosts, 5);

    const formattedDate = targetDate.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric'
    });

    const handleScheduleClick = (creative) => {
        if (onSchedule) {
            onSchedule(creative, targetDate);
        }
    };

    return (
        <div
            className="modal-backdrop"
            onClick={onClose}
            style={{ zIndex: 9999 }}
        >
            <div
                className="modal"
                onClick={(e) => e.stopPropagation()}
                style={{
                    maxWidth: '500px',
                    maxHeight: '80vh',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                {/* Header */}
                <div className="modal-header">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                        <div style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: 'var(--radius-md)',
                            background: 'var(--color-warning)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <Sparkles size={18} color="white" />
                        </div>
                        <div>
                            <h2 className="modal-title" style={{ marginBottom: '2px' }}>
                                Fill This Gap
                            </h2>
                            <p style={{
                                margin: 0,
                                fontSize: 'var(--font-size-sm)',
                                color: 'var(--color-text-secondary)'
                            }}>
                                {formattedDate}
                            </p>
                        </div>
                    </div>
                    <button className="btn btn-icon btn-ghost" onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>

                {/* Content */}
                <div className="modal-body" style={{
                    flex: 1,
                    overflowY: 'auto',
                    padding: 'var(--space-4)'
                }}>
                    {suggestions.length === 0 ? (
                        <div style={{
                            textAlign: 'center',
                            padding: 'var(--space-8) var(--space-4)',
                            color: 'var(--color-text-tertiary)'
                        }}>
                            <Sparkles size={40} style={{ marginBottom: 'var(--space-3)', opacity: 0.5 }} />
                            <p style={{ margin: 0 }}>No creatives available to suggest.</p>
                            <p style={{ margin: 'var(--space-2) 0 0 0', fontSize: 'var(--font-size-sm)' }}>
                                Generate some in Creative Chat!
                            </p>
                        </div>
                    ) : (
                        <>
                            <p style={{
                                margin: '0 0 var(--space-4) 0',
                                fontSize: 'var(--font-size-sm)',
                                color: 'var(--color-text-secondary)'
                            }}>
                                These creatives have the lowest fatigue and haven't been used recently:
                            </p>

                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 'var(--space-3)'
                            }}>
                                {suggestions.map((creative, index) => {
                                    const fatigueColor = getFatigueColor(creative.fatigueLevel);
                                    const fatigueLabel = getFatigueLabel(creative.fatigueLevel);
                                    const thumbnailUrl = creative.url || creative.videoUrl;
                                    const isVideo = creative.isVideo || !!creative.videoUrl;

                                    return (
                                        <div
                                            key={creative.id}
                                            style={{
                                                display: 'flex',
                                                gap: 'var(--space-3)',
                                                padding: 'var(--space-3)',
                                                background: index === 0
                                                    ? 'var(--color-primary-light)'
                                                    : 'var(--color-bg-secondary)',
                                                borderRadius: 'var(--radius-md)',
                                                border: index === 0
                                                    ? '2px solid var(--color-primary)'
                                                    : '1px solid var(--color-border-light)',
                                                cursor: 'pointer',
                                                transition: 'all var(--transition-fast)'
                                            }}
                                            onClick={() => handleScheduleClick(creative)}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.borderColor = 'var(--color-primary)';
                                            }}
                                            onMouseLeave={(e) => {
                                                if (index !== 0) {
                                                    e.currentTarget.style.borderColor = 'var(--color-border-light)';
                                                }
                                            }}
                                        >
                                            {/* Thumbnail */}
                                            <div style={{
                                                width: '80px',
                                                height: '80px',
                                                borderRadius: 'var(--radius-md)',
                                                overflow: 'hidden',
                                                background: 'var(--color-bg-tertiary)',
                                                flexShrink: 0,
                                                position: 'relative'
                                            }}>
                                                {thumbnailUrl ? (
                                                    <img
                                                        src={thumbnailUrl}
                                                        alt={creative.title}
                                                        style={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover'
                                                        }}
                                                    />
                                                ) : (
                                                    <div style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        fontSize: 'var(--font-size-xl)',
                                                        color: 'var(--color-text-tertiary)'
                                                    }}>
                                                        {(creative.title || '?').charAt(0).toUpperCase()}
                                                    </div>
                                                )}

                                                {isVideo && (
                                                    <div style={{
                                                        position: 'absolute',
                                                        top: '50%',
                                                        left: '50%',
                                                        transform: 'translate(-50%, -50%)',
                                                        background: 'rgba(0,0,0,0.6)',
                                                        borderRadius: '50%',
                                                        width: '24px',
                                                        height: '24px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center'
                                                    }}>
                                                        <Play size={12} color="white" fill="white" />
                                                    </div>
                                                )}

                                                {index === 0 && (
                                                    <div style={{
                                                        position: 'absolute',
                                                        top: 'var(--space-1)',
                                                        left: 'var(--space-1)',
                                                        background: 'var(--color-primary)',
                                                        color: 'white',
                                                        fontSize: '9px',
                                                        fontWeight: 600,
                                                        padding: '2px 6px',
                                                        borderRadius: 'var(--radius-sm)'
                                                    }}>
                                                        BEST
                                                    </div>
                                                )}
                                            </div>

                                            {/* Info */}
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <h4 style={{
                                                    margin: '0 0 var(--space-1) 0',
                                                    fontSize: 'var(--font-size-sm)',
                                                    fontWeight: 600,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}>
                                                    {creative.title || 'Untitled'}
                                                </h4>

                                                <div style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 'var(--space-2)',
                                                    marginBottom: 'var(--space-2)'
                                                }}>
                                                    <span style={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        padding: '2px 8px',
                                                        borderRadius: 'var(--radius-full)',
                                                        background: fatigueColor,
                                                        color: 'white',
                                                        fontSize: '10px',
                                                        fontWeight: 600
                                                    }}>
                                                        {creative.usageCount === 0
                                                            ? 'New'
                                                            : `Used ${creative.usageCount}x (${fatigueLabel})`}
                                                    </span>
                                                </div>

                                                <button
                                                    className="btn btn-primary btn-sm"
                                                    style={{ fontSize: 'var(--font-size-xs)' }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleScheduleClick(creative);
                                                    }}
                                                >
                                                    <Calendar size={12} />
                                                    Schedule for {targetDate.toLocaleDateString('en-US', {
                                                        month: 'short',
                                                        day: 'numeric'
                                                    })}
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="modal-footer" style={{
                    borderTop: '1px solid var(--color-border-light)'
                }}>
                    <button className="btn btn-secondary" onClick={onClose}>
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default GapSuggestions;
